import { Injectable, OnDestroy } from '@angular/core';
import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  Observable,
  startWith,
  Subject,
  takeUntil,
  withLatestFrom,
} from 'rxjs';
import { ConfigurationService } from 'src/app/core/configuration.service';
import { CurrentUserService } from 'src/app/core/current-user.service';
import { uuid } from '../../util/uuid';

export interface NewRelicLoaderConfig {
  accountID: string;
  trustKey: string;
  agentID: string;
  licenseKey: string;
  applicationID: string;
}

export interface NewRelicConfiguration {
  enabled: boolean;
  loaderConfig: NewRelicLoaderConfig;
}

const tabId = uuid();

function initializeTelemetry(
  newRelic: BrowserAgent,
  userService: CurrentUserService,
  disposed$: Subject<void>,
) {
  // Set Custon Attributes
  // Add tab ID
  newRelic.setCustomAttribute('tabId', tabId);
  // Set cloud_roleName
  newRelic.setCustomAttribute('roleName', 'website');
  // Always include current href
  newRelic.setCustomAttribute('href', window.location.href);
  // Set user
  userService.valueChanges
    .pipe(
      distinctUntilChanged((a, b) => a?.email === b?.email),
      withLatestFrom(
        userService.authenticated.valueChanges.pipe(startWith(undefined)),
        userService.impersonated.valueChanges,
      ),
      takeUntil(disposed$),
    )
    .subscribe(([currentUser, authenticatedUser, impersonatedUser]) => {
      if (authenticatedUser != null && impersonatedUser != null) {
        newRelic.setUserId(impersonatedUser.email);
        newRelic.setCustomAttribute('user', impersonatedUser.email, true);
        newRelic.setCustomAttribute('impersonatedBy', authenticatedUser.email, true);
      } else {
        newRelic.setUserId(currentUser?.email ?? null);
        newRelic.setCustomAttribute('user', currentUser?.email ?? 'anonymous', true);
        newRelic.setCustomAttribute('impersonatedBy', null, true);
      }
    });
}

@Injectable({
  providedIn: 'root',
})
export class NewRelicService implements OnDestroy {
  public newRelic: BrowserAgent | undefined;
  private _initialized$ = new BehaviorSubject(false);
  private _onDestroy$ = new Subject<void>();

  constructor(private _config: ConfigurationService, private _user: CurrentUserService) {}

  get intialized$(): Observable<boolean> {
    return this._initialized$.asObservable().pipe(filter((init) => init));
  }

  public init() {
    if (this._initialized$.getValue()) {
      return;
    }

    if (this._config.value.newRelic.enabled === true) {
      const options = {
        init: {
          distributed_tracing: {
            enabled: true,
            cors_use_newrelic_header: true,
            cors_use_tracecontext_headers: true,
            allowed_origins: [this._config.value.tcs.graphQLUri],
          },
          privacy: { cookies_enabled: true },
          ajax: { deny_list: ['bam.nr-data.net'] },
        },
        info: {
          beacon: 'bam.nr-data.net',
          errorBeacon: 'bam.nr-data.net',
          licenseKey: this._config.value.newRelic.loaderConfig.licenseKey,
          applicationID: this._config.value.newRelic.loaderConfig.applicationID,
          sa: 1,
        },
        loader_config: this._config.value.newRelic.loaderConfig,
      };
      this.newRelic = new BrowserAgent(options);

      initializeTelemetry(this.newRelic, this._user, this._onDestroy$);

      this._initialized$.next(true);
    }
  }

  ngOnDestroy(): void {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
